import { useEffect, useMemo, useState } from 'react'
import { useSnapshot } from 'valtio'
import sum from 'lodash/sum'
import { CurrentTestState, ScoringState } from '@app/storage'
import { ItemResponse, ItemScoring, ScoringResponse } from '@app/models'
import { isEqualArray } from '../utils'
import {
  ResponseByPracticeTestValueType,
  ResponsesByPracticeTestItem,
  ResponsesByPracticeTestItemResponseDeclaration,
} from '@app/types'
import { findCorrectAnswerItem, findCorrectAnswerResponse } from './itemResponsesScoring'

export const useAssessmentItemScoring = (itemId: string): ItemScoring => {
  const { testScoring } = useSnapshot(ScoringState)

  const [itemScoring, setItemScoring] = useState<ItemScoring>(null)

  useEffect(() => {
    const itemScoring = testScoring?.byItem[itemId] as ItemScoring
    setItemScoring(itemScoring ?? null)
  }, [testScoring, itemId])

  return itemScoring
}

export const useAssessmentItemGroupScoring = (itemId: string): ResponsesByPracticeTestItem => {
  const { groupTestScoring } = useSnapshot(ScoringState)

  const [itemGroupScoring, setItemGroupScoring] = useState<ResponsesByPracticeTestItem>(null)

  useEffect(() => {
    const itemGroupScoring = groupTestScoring?.items?.find(
      item => item.itemId === itemId,
    ) as ResponsesByPracticeTestItem
    setItemGroupScoring(itemGroupScoring ?? null)
  }, [groupTestScoring, itemId])

  return itemGroupScoring
}

export type UseAssessmentItemResponseScoring = {
  scoring: ScoringResponse | null
  correct?: boolean
  raw?: number
  possible?: number
  itemResponse?: ItemResponse
  groupMode: boolean
  groupScoring?: ResponsesByPracticeTestItemResponseDeclaration | null
  groupStatistics?: Record<string, number>
  correctResponse?: string[]
  isPreviewMode?: boolean
  isQuestionPreviewMode?: boolean
}

export const useAssessmentItemResponseScoring = (
  itemId: string,
  responseDeclarationId: string,
): UseAssessmentItemResponseScoring => {
  const { itemsResponses } = useSnapshot(CurrentTestState)
  const {
    groupMode,
    groupTestScoring,
    groupCorrectAnswers,
    isPreviewMode = false,
    isQuestionPreviewMode = false
  } = useSnapshot(ScoringState)
  const itemResponse = itemsResponses
    ?.find(itemResponse => itemResponse.itemId === itemId)
    ?.responses?.find(response => response.responseDeclarationId === responseDeclarationId) as ItemResponse

  const itemScoring = useAssessmentItemScoring(itemId)
  const scoring =
    itemScoring?.correctResponses?.find(
      response => response.responseDeclarationId === responseDeclarationId,
    ) ?? null

  // If there is scoring data, validate answer.
  // It's correct if given answer matches correct or if raw score equals possible
  // (e.g. for ExtendedText interaction).
  let correct

  if (scoring?.response && itemResponse?.response) {
    if (scoring.interactionType === 'extendedText') {
      correct = itemScoring.raw === itemScoring.possible
    } else {
      correct =
        isEqualArray(scoring.response as string[], itemResponse.response as string[]) ||
        itemScoring.raw === itemScoring.possible
    }
  }

  const itemGroupScoring = useAssessmentItemGroupScoring(itemId)
  const groupScoring =
    itemGroupScoring?.responseDeclarations?.find(
      responseDeclaration => responseDeclaration.responseDeclarationId === responseDeclarationId,
    ) ?? null

  let correctResponse = scoring?.response

  if (groupMode) {
    const correctItem = findCorrectAnswerItem(groupCorrectAnswers, itemId)
    correctResponse = findCorrectAnswerResponse(correctItem, responseDeclarationId)?.response
  }

  const groupStatistics = useMemo(() => {
    // In preview mode, return empty statistics to hide percentages
    if (isPreviewMode) {
      return {}
    }

    const numberOfCorrectAnswers = correctResponse?.length || 1
    // Calculate totalSlots: for multiple correct answers, use studentCount * numberOfCorrectAnswers
    // to get percentages relative to all possible answer slots. For POINT_VALUE, use null.
    // For single correct answer, use "null" to calculate relative to studentCount.
    let totalSlots: number
    if (groupScoring?.responseType === ResponseByPracticeTestValueType.POINT_VALUE) {
      totalSlots = null
    } else {
      totalSlots = numberOfCorrectAnswers > 1 ? groupTestScoring?.studentCount * numberOfCorrectAnswers : null
    }

    const groupStatistics: Record<string, number> =
      groupScoring?.responses?.reduce((statistics, response) => {
        const count = 'seqCounts' in response ? response.seqCounts[0].count : response.count
        const responseValue = 'response' in response ? response.response : response.points

        const rawPercentage = totalSlots ? count / totalSlots : count / groupTestScoring?.studentCount

        return {
          ...statistics,
          [responseValue]: Math.round(rawPercentage * 100) / 100,
        }
      }, {}) ?? {}

    // Calculate 'none' to ensure sum equals 1, then round
    const sumOfPercentages = sum(Object.values(groupStatistics))
    groupStatistics['none'] = Math.round(Math.max(0, 1 - sumOfPercentages) * 100) / 100

    return groupStatistics
  }, [
    groupScoring?.responses,
    groupScoring?.responseType,
    groupTestScoring?.studentCount,
    correctResponse?.length,
    isPreviewMode,
  ])

  return {
    scoring,
    correct,
    raw: itemScoring?.raw,
    possible: itemScoring?.possible,
    itemResponse,
    groupMode,
    groupScoring,
    groupStatistics,
    correctResponse,
    isPreviewMode,
    isQuestionPreviewMode
  }
}
